
import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshCw } from 'lucide-react'
import { glossaryData } from '@/data/glossaryData'
import { type GlossaryEntry } from '@/types/glossary'
import { useToast } from '@/components/ui/use-toast'
import { supabase } from '@/integrations/supabase/client'

type Term = {
  term: string
  aiExplanation: string
}

const RandomCryptoTerms = () => {
  const [terms, setTerms] = useState<Term[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()

  const getRandomTerms = () => {
    const allTerms: GlossaryEntry[] = []
    Object.values(glossaryData).forEach((termGroup: GlossaryEntry[]) => {
      allTerms.push(...termGroup)
    })

    const shuffled = allTerms.sort(() => Math.random() - 0.5)
    return shuffled.slice(0, 3)
  }

  const formatExplanation = (text: string) => {
    const cleanText = text.replace(/^[^:]*:\s*/, '')
    return cleanText.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  }

  const fetchExplanations = async () => {
    setIsLoading(true)
    const randomTerms = getRandomTerms()
    
    try {
      console.log('Selected terms:', randomTerms.map(t => t.term))
      
      // This function is now public and doesn't require authentication
      const { data, error } = await supabase.functions.invoke('explain-crypto-terms', {
        body: { terms: randomTerms.map(t => t.term) }
      })

      if (error) {
        console.error('Supabase function error:', error)
        throw new Error(error.message)
      }

      console.log('API response:', data)
      
      if (!data.explanations || !Array.isArray(data.explanations) || data.explanations.length !== 3) {
        console.error('Invalid response format:', data)
        throw new Error('Invalid response format from API')
      }

      setTerms(randomTerms.map((term, index) => ({
        term: term.term,
        aiExplanation: formatExplanation(data.explanations[index] || 'Explanation not available')
      })))
    } catch (error) {
      console.error('Error fetching explanations:', error)
      toast({
        title: "Error",
        description: "Failed to fetch term explanations. Please try again later.",
        variant: "destructive"
      })
      // Set some default explanations from the glossary data as fallback
      setTerms(randomTerms.map(term => ({
        term: term.term,
        aiExplanation: term.definition // Use the static definition as fallback
      })))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchExplanations()
  }, [])

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-center gap-4 mb-6">
          <h2 className="text-2xl font-semibold text-[#4A607B]">
            Random Featured Crypto Terms
          </h2>
          <Button
            variant="ghost"
            size="icon"
            disabled={true}
            className="hover:bg-gray-100"
          >
            <RefreshCw className="h-5 w-5 text-gray-600 animate-spin" />
          </Button>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-20 bg-gray-100 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-center gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-[#4A607B]">
          Random Featured Crypto Terms
        </h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={fetchExplanations}
          disabled={isLoading}
          className="hover:bg-gray-100"
        >
          <RefreshCw className={`h-5 w-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      <div className="grid gap-8 md:grid-cols-3">
        {terms.map((term, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle className="text-xl">{term.term}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600" dangerouslySetInnerHTML={{ __html: term.aiExplanation }} />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default RandomCryptoTerms
